import { forwardRef, ReactNode } from 'react';

import clsx from 'clsx';

import { Flex } from '../Flex';
import { Text } from '../Text';
import { fieldContainer, fieldError, fieldHelper, fieldLabel, fieldRequired } from './Field.css';

export interface FieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Field label text */
  label: string;
  description?: string;
  /** Mark field as required (shows asterisk) */
  required?: boolean;
  /** Show status label (true: show, false: hide) */
  showStatusLabel?: boolean;
  /** Helper text shown below the input */
  helperText?: string;
  /** Error message (overrides helperText when present) */
  error?: string;
  /** Input component (Input, Dropdown, Textarea, etc.) */
  children: ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Custom label className */
  labelClassName?: string;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      label,
      description,
      required = false,
      showStatusLabel = true,
      helperText,
      error,
      children,
      className,
      labelClassName,
      ...props
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    return (
      <div ref={ref} className={clsx(fieldContainer, className)} {...props}>
        <Flex direction="column" gap={2}>
          <label className={clsx(fieldLabel, labelClassName)}>
            <Text textStyle="14_500" color="black">
              {label}
            </Text>
            {showStatusLabel &&
              (required ? (
                <span className={fieldRequired}>
                  <Text textStyle="13_400" color="red600">
                    *
                  </Text>
                </span>
              ) : (
                <Text textStyle="13_400" color="gray500">
                  (선택)
                </Text>
              ))}
          </label>
          {description && (
            <Text textStyle="13_400" color="gray700">
              {description}
            </Text>
          )}
        </Flex>
        <Flex direction="column" gap={2}>
          <Flex direction="column">{children}</Flex>
          {(helperText || error) && (
            <div className={hasError ? fieldError : fieldHelper}>
              <Text textStyle="13_400" color={hasError ? 'red600' : 'gray600'}>
                {error || helperText}
              </Text>
            </div>
          )}
        </Flex>
      </div>
    );
  },
);

Field.displayName = 'Field';
